/**
 * api.ts — window.ManagePatients public JS API.
 *
 * Exposes:
 *   ManagePatients.open(opts?)   — open a popup programmatically
 *   ManagePatients.close()       — close any open popup
 *   ManagePatients.on(event, fn) — subscribe to events
 *   ManagePatients.off(event, fn)— unsubscribe
 *
 * Event names:
 *   'booking-complete'  payload: { appointmentId: string|null, sessionId?: string|null }
 *   'booking-error'     payload: { message: string, sessionId?: string|null }
 *   'step-change'       payload: { step: string, stepIndex: number, totalSteps: number }
 *   'close'             payload: null
 *   'ready'             payload: null (fired when embed.js has initialised)
 *
 * DOM CustomEvents (dispatched on window — no listener registration needed):
 *   managepatients:ready
 *   managepatients:bookingComplete   detail: { appointmentId, sessionId }
 *   managepatients:bookingError      detail: { message, sessionId }
 *   managepatients:stepChange        detail: { step, stepIndex, totalSteps, sessionId }
 *
 * Usage (pre-load queue pattern):
 *   <script src="https://managepatients.com/embed/v1/embed.js" async></script>
 *   <script>
 *     window.ManagePatients = window.ManagePatients || { _q: [] };
 *     window.ManagePatients._q.push(['on', 'booking-complete', function(p) {
 *       console.log('Booked!', p.appointmentId);
 *     }]);
 *   </script>
 *
 * The queue pattern means callers can set up listeners before embed.js loads.
 */

import { EmbedConfig } from './config'
import { getOrCreatePopup } from './modes/popup'

type EventName = 'booking-complete' | 'booking-error' | 'step-change' | 'close' | 'ready'
type Handler    = (payload: unknown) => void

const listeners = new Map<EventName, Set<Handler>>()

export function emitEvent(name: EventName, payload: unknown): void {
  const handlers = listeners.get(name)
  if (!handlers) return
  for (const fn of handlers) {
    try { fn(payload) } catch { /* never let a user handler crash embed.js */ }
  }
}

/** Dispatch a DOM CustomEvent on window (for GTM, FB Pixel, GA4 etc.). */
export function dispatchDOMEvent(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail, bubbles: false }))
  } catch { /* CustomEvent unavailable in very old browsers */ }
}

function on(name: EventName, fn: Handler): void {
  if (!listeners.has(name)) listeners.set(name, new Set())
  listeners.get(name)!.add(fn)
}

function off(name: EventName, fn: Handler): void {
  listeners.get(name)?.delete(fn)
}

export interface OpenOptions {
  org:       string
  mode?:     'popup'
  service?:  string
  staff?:    string
  location?: string
  color?:    string
}

function open(opts: OpenOptions): void {
  const config: EmbedConfig = {
    org:       opts.org,
    mode:      'popup',
    service:   opts.service  ?? null,
    staff:     opts.staff    ?? null,
    location:  opts.location ?? null,
    color:     opts.color    ?? null,
    label:     'Book now',
    position:  'bottom-right',
    sessionId: null,
  }
  getOrCreatePopup(config).open()
}

function close(): void {
  // Close all open popups (there should only ever be one)
  const event = new CustomEvent('mp:close-all')
  window.dispatchEvent(event)
}

/** Replay a command queue that was set up before embed.js loaded. */
function replayQueue(api: ManagePatientsAPI): void {
  const q = (api as unknown as { _q?: unknown[] })._q
  if (!Array.isArray(q)) return
  for (const item of q) {
    if (Array.isArray(item) && typeof item[0] === 'string') {
      const [method, ...args] = item
      if (method === 'on' && typeof item[1] === 'string' && typeof item[2] === 'function') {
        on(item[1] as EventName, item[2] as Handler)
      } else if (method === 'open' && typeof item[1] === 'object') {
        open(item[1] as OpenOptions)
      }
    }
  }
}

export interface ManagePatientsAPI {
  open:    (opts: OpenOptions) => void
  close:   () => void
  on:      (event: EventName, fn: Handler) => void
  off:     (event: EventName, fn: Handler) => void
  version: string
}

declare const __MP_VERSION__: string

export function installAPI(): void {
  const existing = (window as unknown as { ManagePatients?: ManagePatientsAPI & { _q?: unknown[] } }).ManagePatients

  const api: ManagePatientsAPI = {
    open,
    close,
    on,
    off,
    version: __MP_VERSION__,
  }

  // Replay anything queued before the script loaded
  if (existing) replayQueue(existing)

  ;(window as unknown as { ManagePatients: ManagePatientsAPI }).ManagePatients = api

  // Signal ready
  emitEvent('ready', null)
  dispatchDOMEvent('managepatients:ready', null)
}
